import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CreateUserProfileSchema } from '@shared/validation/schemas';
import { CreateUserProfileData } from '@shared/types';
import { useAuthListener } from '../hooks/useAuthListener';
import { auth, db, getFirestoreDb } from '../firebase/firebase';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorBoundary } from '../components/common/ErrorBoundary';

export function CreateProfilePage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profileCreated, setProfileCreated] = useState(false);
  const { user, userProfile } = useAuthListener(auth, db);


  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateUserProfileData>({
    resolver: zodResolver(CreateUserProfileSchema),
    defaultValues: {
      name: user?.displayName || user?.email?.split('@')[0] || '',
      email: user?.email || '',
      role: 'player',
      signup_source: 'web',
    },
  });

  // Update form when user data changes, but only once
  useEffect(() => {
    if (user && !profileCreated) {
      // Form will auto-update with defaultValues
    }
  }, [user, profileCreated]);

  // If user already has a complete profile, redirect them
  useEffect(() => {
    // Only redirect if profile exists AND has a role (complete profile)
    if (userProfile && userProfile.role && !profileCreated) {
      // Profile exists, redirect based on role after a short delay
      // This prevents rapid redirects that cause flashing
      const timer = setTimeout(() => {
        if (userProfile.role === 'superadmin') {
          window.location.replace('/superadmin');
        } else if (userProfile.role === 'admin') {
          window.location.replace('/admin');
        } else {
          window.location.replace('/');
        }
      }, 500);
      
      return () => {
        clearTimeout(timer);
      };
    } else if (userProfile && !userProfile.role) {
      // Profile exists but is incomplete (missing role), allow completion
    }
    return undefined;
  }, [userProfile, profileCreated]);

  const onSubmit = async (data: CreateUserProfileData) => {
    if (!user) {
      setError('No user found. Please sign in again.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Create the user profile using fresh Firebase instance
      const freshDb = getFirestoreDb();
      const userRef = doc(freshDb, 'users', user.uid);
      await setDoc(userRef, {
        ...data,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });
      
      // Mark as created to prevent further re-renders
      setProfileCreated(true);
      
      // Use a more reliable redirect method
      // Wait for the auth listener to pick up the new profile
      let attempts = 0;
      const maxAttempts = 10;
      
      const checkProfile = async () => {
        try {
          const freshDb = getFirestoreDb();
          const userRef = doc(freshDb, 'users', user.uid);
          const profileDoc = await getDoc(userRef);
          const profile = profileDoc.exists() ? profileDoc.data() : null;
          if (profile) {
            // Profile exists, redirect based on role
            if (profile.role === 'superadmin') {
              window.location.replace('/superadmin');
            } else if (profile.role === 'admin') {
              window.location.replace('/admin');
            } else {
              window.location.replace('/');
            }
            return;
          }
          
          attempts++;
          if (attempts < maxAttempts) {
            // Wait a bit and try again
            setTimeout(checkProfile, 500);
          } else {
            // Fallback: redirect to rooms anyway after max attempts
            window.location.replace('/');
          }
        } catch (err) {
          // Fallback: redirect to rooms anyway
          window.location.replace('/');
        }
      };
      
      // Start checking for the profile
      setTimeout(checkProfile, 1000);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create profile');
      setLoading(false);
    }
  };

  // If user already has a complete profile, show loading while redirecting
  if (userProfile && userProfile.role && !profileCreated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="text-gray-600 mt-4">Redirecting...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Authentication Required</h1>
          <p className="text-gray-600 mb-4">Please sign in to create your profile.</p>
          <a href="/login" className="btn btn-primary">Sign In</a>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Complete Your Profile</h1>
            <p className="mt-2 text-gray-600">
              We need a bit more information to set up your account.
            </p>
          </div>

          <div className="bg-white py-8 px-6 shadow rounded-lg">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Full Name *
                </label>
                <input
                  {...register('name')}
                  type="text"
                  id="name"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Enter your full name"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address *
                </label>
                <input
                  {...register('email')}
                  type="email"
                  id="email"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Enter your email"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  Phone Number (Optional)
                </label>
                <input
                  {...register('phone')}
                  type="tel"
                  id="phone"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Enter your phone number"
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Entering your number opts you in for poker waitlist & promo texts. Msg/data rates may apply.
                </p>
              </div>

              <div>
                <label htmlFor="alias" className="block text-sm font-medium text-gray-700">
                  Poker Alias (Optional)
                </label>
                <input
                  {...register('alias')}
                  type="text"
                  id="alias"
                  maxLength={12}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Enter your poker alias (max 12 characters)"
                />
                {errors.alias && (
                  <p className="mt-1 text-sm text-red-600">{errors.alias.message}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  This will be displayed on waitlists instead of your full name.
                </p>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {loading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Creating Profile...</span>
                    </>
                  ) : (
                    'Create Profile'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
}